"use client";

import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { TOKEN_STORAGE_KEY } from "@/lib/theme";
import { User } from "@/lib/types";

type AuthContextValue = {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshSession = async () => {
    const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_STORAGE_KEY) : null;
    if (!token) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.session();
      setUser(response.user);
    } catch {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void refreshSession();
  }, []);

  // Любой 401 из api (event из apiFetchRaw) = протухшая сессия → чистый
  // разлогин (PortalFrame уведёт на /login) вместо случайных ошибок в виджетах.
  useEffect(() => {
    const onUnauthorized = () => {
      if (!localStorage.getItem(TOKEN_STORAGE_KEY)) return; // уже разлогинены
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      setUser(null);
    };
    window.addEventListener("portal:unauthorized", onUnauthorized);
    return () => window.removeEventListener("portal:unauthorized", onUnauthorized);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      login: async (email, password) => {
        const response = await api.login(email, password);
        localStorage.setItem(TOKEN_STORAGE_KEY, response.token);
        setUser(response.user);
      },
      logout: async () => {
        try {
          await api.logout();
        } catch {
          // ignore logout backend errors in scaffold mode
        } finally {
          localStorage.removeItem(TOKEN_STORAGE_KEY);
          setUser(null);
        }
      },
      refreshSession,
    }),
    [user, isLoading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
